import { useEffect, useRef, useState } from 'react';
import { STATS, slugify, wallSource } from '../editor/buffers';
import { PROJECTS, SOCIALS } from '../constants';

const FONT_SIZE = 12;
const CELL_H = 15;
const COL_CHARS = 84;
const COL_GAP = 4;
const SPOT_R = 170;
const KEYWORDS = /^(export|const|function|return|import|from|null|true|false)$/;
const TOKEN = /(\/\/.*$)|("(?:[^"\\]|\\.)*")|(\b\d+\b)|([A-Za-z_$][\w$]*)|(\s+)|(.)/g;

const SOURCE = wallSource().split('\n');

function tokenize(line) {
    const runs = [];
    let m;
    TOKEN.lastIndex = 0;
    while ((m = TOKEN.exec(line))) {
        let role = 'ink';
        if (m[1]) role = 'dim';
        else if (m[2]) role = 'str';
        else if (m[3]) role = 'num';
        else if (m[4]) role = KEYWORDS.test(m[4]) ? 'kw' : /^[A-Z]/.test(m[4]) ? 'ty' : 'ink';
        else if (m[5]) role = null;
        else role = 'chrome';
        runs.push({ text: m[0], role });
    }
    return runs;
}

const TOKENS = SOURCE.map(tokenize);

function cssVar(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
}

// Paint the whole wall once at a given alpha so frames only blit.
function paintLayer(w, h, dpr, cellW, alpha) {
    const c = document.createElement('canvas');
    c.width = w * dpr;
    c.height = h * dpr;
    const ctx = c.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.font = `${FONT_SIZE}px "IBM Plex Mono", ui-monospace, monospace`;
    ctx.textBaseline = 'top';
    ctx.globalAlpha = alpha;
    const colors = Object.fromEntries(['ink', 'dim', 'chrome', 'kw', 'str', 'num', 'ty'].map((k) => [k, cssVar(k)]));
    const colW = (COL_CHARS + COL_GAP) * cellW;
    const cols = Math.ceil(w / colW) + 1;
    const rows = Math.ceil(h / CELL_H) + 1;
    for (let c2 = 0; c2 < cols; c2++) {
        const offset = (c2 * 37) % TOKENS.length;
        for (let r = 0; r < rows; r++) {
            const runs = TOKENS[(r + offset) % TOKENS.length];
            const y = r * CELL_H + 1;
            const x0 = c2 * colW + cellW * 2;
            let ch = 0;
            for (const run of runs) {
                if (ch >= COL_CHARS) break;
                const text = run.text.slice(0, COL_CHARS - ch);
                if (run.role) {
                    ctx.fillStyle = colors[run.role];
                    ctx.fillText(text, x0 + ch * cellW, y);
                }
                ch += text.length;
            }
        }
    }
    return { canvas: c, colW, rows };
}

function Wall({ themeName }) {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return undefined;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let w = 0, h = 0, dpr = 1, cellW = 7.2;
        let base, lit, spot, spotCtx;
        let pointer = { x: -9999, y: -9999 };
        let cursor = { col: 1, row: 4, ch: 0 };
        let raf = 0, timer = 0, cancelled = false;

        const build = () => {
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            w = canvas.clientWidth;
            h = canvas.clientHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.font = `${FONT_SIZE}px "IBM Plex Mono", ui-monospace, monospace`;
            cellW = ctx.measureText('M').width;
            base = paintLayer(w, h, dpr, cellW, cssVar('bg').startsWith('#f') ? 0.22 : 0.16);
            lit = paintLayer(w, h, dpr, cellW, 0.95);
            spot = document.createElement('canvas');
            spot.width = spot.height = SPOT_R * 2 * dpr;
            spotCtx = spot.getContext('2d');
            const visibleCols = Math.max(1, Math.floor(w / base.colW));
            cursor = { col: Math.floor(visibleCols / 2), row: Math.floor(base.rows * 0.42), ch: 0 };
            draw();
        };

        const lineLength = (col, row) => {
            const offset = (col * 37) % TOKENS.length;
            return Math.min(COL_CHARS, SOURCE[(row + offset) % SOURCE.length].length);
        };

        const draw = () => {
            raf = 0;
            ctx.clearRect(0, 0, w, h);
            ctx.drawImage(base.canvas, 0, 0, w, h);

            // Typed-so-far trail on the cursor's line.
            const cx = cursor.col * base.colW + cellW * 2;
            const cy = cursor.row * CELL_H;
            if (cursor.ch > 0) {
                const tw = cursor.ch * cellW;
                ctx.drawImage(lit.canvas, cx * dpr, cy * dpr, tw * dpr, CELL_H * dpr, cx, cy, tw, CELL_H);
            }
            ctx.fillStyle = cssVar('accent');
            ctx.fillRect(cx + cursor.ch * cellW, cy, cellW, CELL_H);

            // Soft spotlight that reveals the wall under the pointer.
            if (pointer.x > -SPOT_R) {
                const s = SPOT_R * 2;
                spotCtx.globalCompositeOperation = 'source-over';
                spotCtx.clearRect(0, 0, spot.width, spot.height);
                spotCtx.drawImage(lit.canvas, (pointer.x - SPOT_R) * dpr, (pointer.y - SPOT_R) * dpr, s * dpr, s * dpr, 0, 0, spot.width, spot.height);
                spotCtx.globalCompositeOperation = 'destination-in';
                const g = spotCtx.createRadialGradient(spot.width / 2, spot.height / 2, 0, spot.width / 2, spot.height / 2, spot.width / 2);
                g.addColorStop(0, 'rgba(0,0,0,0.9)');
                g.addColorStop(1, 'rgba(0,0,0,0)');
                spotCtx.fillStyle = g;
                spotCtx.fillRect(0, 0, spot.width, spot.height);
                ctx.drawImage(spot, pointer.x - SPOT_R, pointer.y - SPOT_R, s, s);
            }
        };

        const schedule = () => {
            if (!raf) raf = requestAnimationFrame(draw);
        };

        const tick = () => {
            const len = lineLength(cursor.col, cursor.row);
            if (cursor.ch < len) {
                cursor.ch += 1;
                timer = setTimeout(tick, 45 + Math.random() * 90);
            } else {
                cursor.row = (cursor.row + 1) % Math.max(1, base.rows - 2);
                cursor.ch = 0;
                timer = setTimeout(tick, 380);
            }
            schedule();
        };

        const onMove = (e) => {
            pointer = { x: e.clientX, y: e.clientY };
            schedule();
        };
        const onLeave = () => {
            pointer = { x: -9999, y: -9999 };
            schedule();
        };

        const start = async () => {
            try {
                await document.fonts.load(`${FONT_SIZE}px "IBM Plex Mono"`);
            } catch (e) {
                // fall back to the system monospace
            }
            if (cancelled) return;
            build();
            if (!reduced) timer = setTimeout(tick, 600);
        };

        start();
        window.addEventListener('resize', build);
        window.addEventListener('pointermove', onMove);
        document.addEventListener('pointerleave', onLeave);
        return () => {
            cancelled = true;
            clearTimeout(timer);
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', build);
            window.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerleave', onLeave);
        };
    }, [themeName]);

    return <canvas ref={ref} className="wall" aria-hidden="true" />;
}

const GLYPHS = '!<>-_\\/[]{}=+*^?#01ab';

// Characters resolve left to right out of noise, like a decoder locking on.
function Decode({ text }) {
    const [out, setOut] = useState(text);
    useEffect(() => {
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
        const lock = [...text].map((_, i) => 180 + i * 55 + Math.random() * 160);
        const start = performance.now();
        let raf = 0;
        const frame = (now) => {
            const t = now - start;
            setOut(
                [...text]
                    .map((c, i) => (c === ' ' || t >= lock[i] ? c : GLYPHS[Math.floor(Math.random() * GLYPHS.length)]))
                    .join('')
            );
            if (t < lock[lock.length - 1]) raf = requestAnimationFrame(frame);
        };
        raf = requestAnimationFrame(frame);
        return () => cancelAnimationFrame(raf);
    }, [text]);
    return (
        <span aria-label={text}>
            <span aria-hidden="true">{out}</span>
        </span>
    );
}

export default function Intro({ theme, onEnter, onTheme }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (e.key === 'Enter' && e.target === document.body) {
                e.preventDefault();
                onEnter();
            } else if (e.key === 't') {
                onTheme();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onEnter, onTheme]);

    return (
        <main className="intro">
            <h1 className="sr-only">Adeboye Bello — full-stack software engineer</h1>
            <Wall themeName={theme.name} />

            <header className="intro-top">
                <a className="brand" href="#/">
                    <span className="brand-mark" aria-hidden="true">ab</span>
                    <span>adeboye.bello</span>
                </a>
                <nav className="intro-links" aria-label="Elsewhere">
                    {SOCIALS.slice(0, 2).map((s) => (
                        <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer">{s.name}</a>
                    ))}
                </nav>
            </header>

            <section className="intro-card">
                <p className="intro-path">~/adeboye — portfolio</p>
                <p className="intro-name"><Decode text="Adeboye Bello" /></p>
                <p className="intro-role">full-stack software engineer · abuja, nigeria</p>
                <p className="intro-facts">
                    <span><b>{STATS.projects}</b> projects</span>
                    <span><b>{STATS.live}</b> live</span>
                    <span><b>{STATS.years}+</b> yrs shipping</span>
                </p>
                <p className="intro-note">{'// this site is a code editor — open it to browse my work.'}</p>
                <div className="intro-actions">
                    <button type="button" className="enter" onClick={onEnter}>enter the editor ↩</button>
                    <button type="button" className="theme-key" onClick={onTheme} aria-label="Change theme">
                        <span className="command-glyph" aria-hidden="true" />:theme <span className="theme-name">{theme.name}</span>
                    </button>
                </div>
                <p className="intro-jump">
                    <span className="dimmed">or jump to </span>
                    {PROJECTS.slice(0, 3).map((p) => (
                        <a key={p.title} href={`#/projects/${slugify(p.title)}.md`}>{slugify(p.title)}</a>
                    ))}
                </p>
            </section>

            <div className="intro-status" aria-hidden="true">
                <p className="statusline">
                    <span className="mode">NORMAL</span>
                    <span className="path">~/adeboye/portfolio.js</span>
                    <span className="meta">utf-8 · javascript · 1:1</span>
                </p>
                <p className="cmdline">
                    <span className="dimmed">-- press </span>↵<span className="dimmed"> to open the editor, </span>t<span className="dimmed"> to change theme --</span>
                </p>
            </div>
        </main>
    );
}
